import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { FormLayout, Form, Button, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { amendWorkSecurityRole } from "services/work-security.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import WorkSecurityRulesEditor, {
  buildOrderedSecurityRules,
} from "./WorkSecurityRulesEditor";

interface WorkSecurityRulesAmendModalProps {
  roleId: string;
  initialRules: string[];
  onSuccess?: () => void;
}

export default function WorkSecurityRulesAmendModal({
  roleId,
  initialRules = [],
  onSuccess,
}: WorkSecurityRulesAmendModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [rules, setRules] = useState<string[]>([""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialRules && initialRules.length > 0) {
      setRules(initialRules);
    }
  }, [initialRules]);

  const handleFormSubmit = async () => {
    if (!companyId || !roleId) return;

    const filteredRules = buildOrderedSecurityRules(rules);

    if (filteredRules.length === 0) {
      setError(
        t("rules_required", { defaultValue: "At least one rule is required" })
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      await amendWorkSecurityRole(companyId, roleId, {
        rules: filteredRules,
      });

      // Close modal
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error("Failed to amend rules", err);
      setError(
        err?.response?.data?.message ||
          t("failed_amend_role", { defaultValue: "Failed to update rules." })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        <Banner
          title={t("amend_rules_warning_title", {
            defaultValue: "Important Notice: Amending Rules",
          })}
          tone="warning"
        >
          <p>
            {t("amend_rules_warning_desc", {
              defaultValue:
                "Amending rules will archive the current active document under 'Previous Versions' and prompt you to manually generate or upload a new safety document. All signature statuses will reset and assigned users will receive a new signature request once the new document is generated or uploaded.",
            })}
          </p>
        </Banner>

        <WorkSecurityRulesEditor
          rules={rules}
          onRulesChange={setRules}
          disabled={saving}
          maxHeightClassName="md:max-h-[300px]"
        />

        <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
          <Button
            onClick={() => {
              dispatch(setIsModalxOpen(false));
              dispatch(setDestroy());
            }}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button variant="primary" onClick={handleFormSubmit} loading={saving}>
            {t("save", { defaultValue: "Save" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
